import ShopifyCartSync from '../helpers/shopifyCartSync';
import { ON_CONVERSATION_CREATED } from '../constants/widgetBusEvents';

/**
 * Cart State Tracking Mixin
 * 
 * Tracks Shopify cart state and forwards cart operations from backend to theme widget.
 * Cart modifications are handled by the theme widget via Ajax API.
 * 
 * Debug Logging:
 * To enable debug logs, set window.CART_SYNC_DEBUG = true in the browser console.
 */
export default {
  data() {
    return {
      ajaxCartToken: null,
      storefrontCartId: null,
      debugLogging: window.CART_SYNC_DEBUG || false
    };
  },
  
  computed: {
    conversationId() {
      return this.$store.getters['conversationAttributes/getConversationParams']?.id;
    }
  },
  
  // No watchers needed anymore since cart token is sent with conversation creation
  
  mounted() {
    // Initialize cart sync when component mounts
    ShopifyCartSync.init();
    
    // Listen for cart updates from parent shop
    window.addEventListener('shopifyCartUpdated', this.handleShopifyCartUpdate);
    
    // No need to listen for conversation creation anymore
    
    // Listen for cart updates from backend (via ActionCable)
    window.bus.$on('cart:updated', this.handleBackendCartUpdate);
    
    // Check for stored ajax token from previous session
    const storedToken = localStorage.getItem('shopify_ajax_cart_token');
    if (storedToken) {
      this.ajaxCartToken = storedToken;
      this.debugLog('📦 Found stored Ajax cart token:', storedToken);
    }
    
    // Request cart state from parent shop
    ShopifyCartSync.requestCartState();
    
    // Also request again after a short delay in case parent wasn't ready
    setTimeout(() => {
      if (!this.ajaxCartToken) {
        this.debugLog('🔄 No cart token yet, requesting again...');
        ShopifyCartSync.requestCartState();
      }
    }, 500);
  },
  
  beforeDestroy() {
    window.bus.$off('cart:updated', this.handleBackendCartUpdate);
    window.removeEventListener('shopifyCartUpdated', this.handleShopifyCartUpdate);
  },
  
  methods: {
    // Debug logging helper - optimized to skip args processing when disabled
    debugLog(...args) {
      // Early return to avoid any processing when debugging is off
      if (!this.debugLogging && !window.CART_SYNC_DEBUG) return;
      console.log('[CartSync]', ...args);
    },
    
    // Debug warning helper
    debugWarn(...args) {
      if (!this.debugLogging && !window.CART_SYNC_DEBUG) return;
      console.warn('[CartSync]', ...args);
    },
    
    // Debug error helper - always show errors in development
    debugError(...args) {
      if (!this.debugLogging && !window.CART_SYNC_DEBUG && process.env.NODE_ENV !== 'development') return;
      console.error('[CartSync]', ...args);
    },
    
    
    // Handle cart updates from parent shop
    handleShopifyCartUpdate(event) {
      const { cart, ajax_token } = event.detail;
      this.debugLog('🛒 Received cart update from shop:', cart);
      this.debugLog('🔑 Ajax cart token:', ajax_token);
      
      // Only process if token is new or changed
      if (ajax_token && ajax_token !== this.ajaxCartToken) {
        const previousToken = this.ajaxCartToken;
        this.ajaxCartToken = ajax_token;
        
        // Store in localStorage so it persists and will be included in conversation creation
        localStorage.setItem('shopify_ajax_cart_token', ajax_token);
        this.debugLog('💾 Stored NEW Ajax cart token locally (was:', previousToken, ')');
      } else if (ajax_token === this.ajaxCartToken) {
        this.debugLog('✅ Token unchanged, skipping update');
      }
      
      // Cart state is available here if needed for display purposes
      // this.currentShopifyCart = cart;
    },
    
    
    handleBackendCartUpdate(data) {
      this.debugLog('🚀 Received cart update from backend:', data);
      
      const cartData = data;
      
      if (cartData && cartData.type === 'cart_sync') {
        this.debugLog('📤 Forwarding cart sync to parent shop:', cartData);
        ShopifyCartSync.forwardCartSyncToParent(cartData);
      }
    },
    
    shouldAutoOpenCart() {
      return false;
    }
  }
};